import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AllQuestions from '../AllData/AllQuestions';
import Loading from '../home/Loading';
import '../../pagescss/SubjectProgress.css';
import TopNavbar from '../home/TopNavbar';
import TopHeader from '../home/TopHeader';

// A helper function to get a consistent color for a subject
const getSubjectColor = (index) => {
    const colors = [
        '#3498db', '#2ecc71', '#e74c3c', '#f39c12', '#9b59b6',
        '#1abc9c', '#d35400', '#34495e', '#16a085', '#c0392b'
    ];
    return colors[index % colors.length];
};

const SubjectProgress = () => {
    const [progressData, setProgressData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalQuestions, setTotalQuestions] = useState(0);
    const [totalSaved, setTotalSaved] = useState(0);

    // Use useMemo for the question map to avoid re-creating it on every render
    const questionMap = useMemo(() => {
        const map = {};
        AllQuestions.forEach(q => {
            map[q._id.$oid] = q; // Adjusted to handle your specific _id format
        });
        return map;
    }, []);

    useEffect(() => {
        const calculateProgress = () => {
            try {
                const savedQuestionIds = JSON.parse(localStorage.getItem('saveQuestionsIdInLocalStorage')) || [];
                setTotalSaved(savedQuestionIds.length);

                const questionsWithSubjects = AllQuestions.filter(q => q.subject);
                const allSubjects = [...new Set(questionsWithSubjects.map(q => q.subject))];
                
                setTotalQuestions(AllQuestions.length);

                const subjectStats = allSubjects.map(subject => {
                    const subjectQuestions = questionsWithSubjects.filter(q => q.subject === subject);
                    const savedQuestions = savedQuestionIds.filter(questionId => {
                        const question = questionMap[questionId];
                        return question && question.subject === subject;
                    }).length;

                    const subjectPercentage = subjectQuestions.length > 0 ? 
                        (subjectQuestions.length / AllQuestions.length) * 100 : 0;
                    const savedPercentage = subjectQuestions.length > 0 ?
                        (savedQuestions / subjectQuestions.length) * 100 : 0;

                    return {
                        subject,
                        totalQuestions: subjectQuestions.length,
                        saved: savedQuestions,
                        subjectPercentage: Math.round(subjectPercentage * 10) / 10,
                        savedPercentage: Math.round(savedPercentage * 10) / 10
                    };
                });

                // Sort subjects by total number of questions, then by saved percentage.
                // This ensures subjects with questions but no saves still appear.
                const sortedStats = subjectStats.sort((a, b) => b.totalQuestions - a.totalQuestions);
                setProgressData(sortedStats);
            } catch (error) {
                console.error("Error calculating progress:", error);
                setProgressData([]);
            } finally {
                setLoading(false);
            }
        };

        // Add an event listener for storage changes to update progress in real-time
        window.addEventListener('storage', calculateProgress);
        calculateProgress();

        return () => {
            // Clean up the event listener on component unmount
            window.removeEventListener('storage', calculateProgress);
        };
    }, [questionMap]);

    // CORRECT: Place the useMemo hook for the gradient at the top level
    const mainProgressGradient = useMemo(() => {
        if (totalSaved === 0) return '#f0f0f0';

        let cumulativePercentage = 0;
        const segments = progressData
            .filter(subject => subject.saved > 0)
            .map((subject, index) => {
                const start = cumulativePercentage;
                const segmentPercentage = (subject.saved / totalSaved) * 100;
                cumulativePercentage += segmentPercentage;
                return `${getSubjectColor(index)} ${start * 3.6}deg ${cumulativePercentage * 3.6}deg`;
            });

        return `conic-gradient(${segments.join(', ')})`;
    }, [progressData, totalSaved]);

    if (loading) {
        return <Loading message="Calculating your progress..." />;
    }

    const totalPercentage = totalQuestions > 0 ? Math.round((totalSaved / totalQuestions) * 100) : 0;

    return (
        <div className="progress-container">
            <TopNavbar name = {"Your study progress " + "(" +totalPercentage + "%" + ")"} />
           <TopHeader name = {"Your study progress " + "(" +totalPercentage + "%" + ")"} />

            {progressData.length === 0 && totalQuestions === 0 ? (
                <div className="empty-progress">
                    <div className="empty-icon">
                        <i className="fas fa-exclamation-circle" style={{fontSize: '3rem', color: '#e74c3c'}}></i>
                    </div>
                    <h3>No Questions Available</h3>
                    <p>The question database is currently empty. Please check back later.</p>
                </div>
            ) : totalSaved === 0 ? (
                <div className="empty-progress">
                    <div className="empty-icon">
                        <i className="fas fa-bookmark" style={{fontSize: '3rem', color: '#7f8c8d'}}></i>
                    </div>
                    <h3>No Saved Questions Yet</h3>
                    <p>Questions you save will appear here and contribute to your progress.</p>
                    <Link to="/practice-questions" className="practice-btn">
                        Start Practicing Now
                    </Link>
                </div>
            ) : (
                <>
                    <div className="main-progress-section">
                        <h2>Overall Progress: {totalPercentage}%</h2>
                        <p>{totalSaved} out of {totalQuestions} questions saved</p>

                        <div className="main-progress-container">
                            <div
                                className="main-progress-circle"
                                style={{
                                    background: mainProgressGradient
                                }}
                            >
                                <div className="main-progress-inner">
                                    <span className="main-percentage">{totalPercentage}%</span>
                                </div>
                            </div>

                            <div className="progress-legend">
                                {progressData
                                    .filter(subject => subject.saved > 0)
                                    .map((subject, index) => (
                                        <div key={index} className="legend-item">
                                            <span
                                                className="legend-color"
                                                style={{ backgroundColor: getSubjectColor(index) }}
                                            ></span>
                                            <span className="legend-text">
                                                {subject.subject}: {subject.saved} of {subject.totalQuestions} ({Math.round((subject.saved / totalSaved) * 100)}% of saved)
                                            </span>
                                        </div>
                                    ))
                                }
                            </div>
                        </div>
                    </div>
                    
                    <hr className="separator" />

                    <div className="circle-grid">
                        {progressData.map((subject, index) => (
                            <div key={index} className="circle-card">
                                <div className="circle-progress">
                                    <div
                                        className="circle-outer"
                                        style={{
                                            background: `conic-gradient(
                                                ${getSubjectColor(index)} ${subject.savedPercentage * 3.6}deg,
                                                #f0f0f0 ${subject.savedPercentage * 3.6}deg 360deg
                                            )`
                                        }}
                                    >
                                        <div className="circle-inner">
                                            <span className="percentage">{subject.savedPercentage}%</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="subject-info">
                                    <h3>{subject.subject}</h3>
                                    <div className="stats">
                                        <span className="solved">{subject.saved} of {subject.totalQuestions}</span>
                                        <span className="weight">({subject.subjectPercentage}% of total)</span>
                                    </div>
                                    <Link
                                        to={`/practice-question-topics/${subject.subject}`}
                                        className="practice-btn"
                                    >
                                        Practice
                                    </Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default SubjectProgress;